import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { getETaxNotes } from "../services";
import { ETaxDocumentFilter, ETaxNote } from "../types/e-tax.type";
import { numberWithCommas } from "../utils";
import { useDebounce } from "../utils/hooks/useDebounce";
import { COMPACT_QUERY } from "../utils/hooks/useCompact";
import ETaxPagination from "../components/ETax/Pagination";
import DocumentFilter from "../components/ETax/DocumentFilter";
import ConfirmSendModal from "../components/ETax/ConfirmSendModal";
import SuccessModal from "../components/ETax/SuccessModal";
import SendProblemModal, { SendProblem } from "../components/ETax/SendProblemModal";
import SendPageHeader from "../components/ETax/SendPageHeader";
import SendSummaryFooter from "../components/ETax/SendSummaryFooter";
import { useSendDateRange } from "../components/ETax/useSendDateRange";
import { ETAX_NOTE_SEND_PATH } from "../components/ETax/sendPaths";
import {
  NOTE_DEFAULT_STATUS_FILTER,
  NOTE_DOC_TYPE_FILTER,
  NOTE_ETAX_STATUS_FILTER,
} from "../components/ETax/documentMeta";
import {
  SendResultSummary,
  buildSendSummary,
  clampPage,
} from "../components/ETax/sendBatch";
import NoteSendTable from "../components/ETax/Notes/NoteSendTable";
import { NOTE_DOC_TYPES } from "../components/ETax/Notes/noteSend";
import { sendNoteBatches } from "../components/ETax/Notes/sendNoteBatches";

const compact = "@media " + COMPACT_QUERY;
const DEFAULT_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 500;
const ALL = "all";
const NOTE_NOUN = "เอกสาร";

/**
 * ส่งเอกสาร E-Tax, second tab: the credit / debit notes. It opens on the notes still owed to
 * e-Tax, like the bill tab, and the สถานะ filter reaches the rest. A note is picked only when
 * the server says it can be sent; every other note says why it cannot, in its status cell.
 * Sending a failed note again is the same pick-and-confirm.
 */
export default function ETaxNoteSendPage() {
  const { startDate, endDate, changeDateRange, dateSearch, isRangeInvalid } =
    useSendDateRange();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(DEFAULT_LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [notes, setNotes] = useState<ETaxNote[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [selected, setSelected] = useState<Record<string, ETaxNote>>({});

  const [documentType, setDocumentType] = useState<string>(ALL);
  const [etaxStatus, setEtaxStatus] = useState<string>(NOTE_DEFAULT_STATUS_FILTER);
  const [branchCode, setBranchCode] = useState<string>(ALL);
  const [routeCode, setRouteCode] = useState<string>(ALL);
  const [customerName, setCustomerName] = useState<string>("");
  const [taxId, setTaxId] = useState<string>("");

  const customerNameSearch = useDebounce(customerName, SEARCH_DEBOUNCE_MS);
  const taxIdSearch = useDebounce(taxId, SEARCH_DEBOUNCE_MS);

  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [successOpen, setSuccessOpen] = useState<boolean>(false);
  const [sendResult, setSendResult] = useState<SendResultSummary | null>(null);
  const [sendProblem, setSendProblem] = useState<SendProblem | null>(null);

  const filter = useMemo<ETaxDocumentFilter>(
    () => ({
      dateFrom: startDate,
      dateTo: endDate,
      documentType,
      etaxStatus,
      branchCode,
      routeCode,
      customerName: customerNameSearch,
      taxId: taxIdSearch,
    }),
    [
      startDate,
      endDate,
      documentType,
      etaxStatus,
      branchCode,
      routeCode,
      customerNameSearch,
      taxIdSearch,
    ],
  );

  // A selection belongs to the filter it was made under. Notes picked on another date range,
  // branch or status are no longer on screen, and sending them would surprise the operator.
  useEffect(() => {
    setSelected({});
  }, [filter]);

  // Only the newest request may write state: a slower earlier response must not repaint the
  // table with rows from the previous filter.
  const requestSeq = useRef<number>(0);

  const fetchNotes = useCallback(async () => {
    // A start-after-end range matches nothing; DocumentFilter already flags it on the pickers.
    if (isRangeInvalid) return;

    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    setLoading(true);
    try {
      const {
        data: { data },
      } = await getETaxNotes(page, limit, filter);
      if (requestSeq.current !== seq) return;
      const rows = data?.rows ?? [];
      const count = data?.count ?? 0;
      // Sending can shrink the list under the current page. Step back to the last page that
      // still has rows; the page change refetches on its own.
      const lastUsefulPage = clampPage(page, count, limit);
      if (rows.length === 0 && lastUsefulPage !== page) {
        setPage(lastUsefulPage);
        return;
      }
      setNotes(rows);
      setTotalCount(count);
    } catch {
      // The axios interceptor surfaces the error toast; reset to an empty state so the table
      // is never stuck on the loading skeleton.
      if (requestSeq.current !== seq) return;
      setNotes([]);
      setTotalCount(0);
    } finally {
      if (requestSeq.current === seq) setLoading(false);
    }
  }, [page, limit, filter, isRangeInvalid]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const selectedNotes = useMemo(() => Object.values(selected), [selected]);
  const selectedSummary = useMemo(
    () => buildSendSummary(selectedNotes),
    [selectedNotes],
  );

  const handleFilterChange = () => setPage(1);

  const handleToggleNote = (note: ETaxNote) => {
    setSelected((previous) => {
      const next = { ...previous };
      if (next[note.transaction_uuid]) {
        delete next[note.transaction_uuid];
      } else {
        next[note.transaction_uuid] = note;
      }
      return next;
    });
  };

  const handleToggleAllOnPage = (checked: boolean) => {
    setSelected((previous) => {
      const next = { ...previous };
      notes.forEach((note) => {
        if (!checked) {
          delete next[note.transaction_uuid];
        } else if (note.can_send) {
          next[note.transaction_uuid] = note;
        }
      });
      return next;
    });
  };

  const handleOpenConfirm = () => setConfirmOpen(true);
  const handleCloseConfirm = () => setConfirmOpen(false);
  const handleCloseSuccess = () => setSuccessOpen(false);
  const handleCloseProblem = () => setSendProblem(null);

  const handleConfirmSend = async () => {
    if (selectedNotes.length === 0 || sending) return;

    setSending(true);
    try {
      const outcome = await sendNoteBatches(selectedNotes);
      // Nothing was written and the toast has said why: the dialog stays open for another try.
      if (outcome.kind === "RETRY") return;

      setConfirmOpen(false);
      if (outcome.kind === "SENT") {
        setSendResult(outcome.result);
        setSuccessOpen(true);
        setSelected({});
      } else {
        // Notes already written, or refused by the gate, must not be sent again by the next press.
        const leavingUuids = new Set(outcome.leavingUuids);
        setSelected((previous) =>
          Object.fromEntries(
            Object.entries(previous).filter(([uuid]) => !leavingUuids.has(uuid)),
          ),
        );
        setSendProblem(outcome.problem);
      }
      fetchNotes();
    } finally {
      setSending(false);
    }
  };

  return (
    // Bottom padding clears the fixed summary footer so the table's last rows and
    // pagination stay reachable above it (the footer is position: fixed).
    <Box sx={{ px: 0, pt: "8px", pb: "88px", [compact]: { pb: "70px" } }}>
      <SendPageHeader current={ETAX_NOTE_SEND_PATH} dateSearch={dateSearch} />

      <Paper>
        <DocumentFilter
          documentType={documentType}
          setDocumentType={setDocumentType}
          documentTypeOptions={NOTE_DOC_TYPE_FILTER}
          etaxStatus={etaxStatus}
          setEtaxStatus={setEtaxStatus}
          statusOptions={NOTE_ETAX_STATUS_FILTER}
          branchCode={branchCode}
          setBranchCode={setBranchCode}
          routeCode={routeCode}
          setRouteCode={setRouteCode}
          customerName={customerName}
          setCustomerName={setCustomerName}
          taxId={taxId}
          setTaxId={setTaxId}
          dateFrom={startDate}
          dateTo={endDate}
          onChangeDateRange={changeDateRange}
          onChange={handleFilterChange}
        />

        <Typography variant="body2" color="text.secondary" mb={2}>
          ทั้งหมด{" "}
          <Box component="span" fontWeight={700} color="text.primary">
            {numberWithCommas(totalCount)}
          </Box>{" "}
          รายการ · เลือกแล้ว{" "}
          <Box component="span" fontWeight={700} color="text.primary">
            {numberWithCommas(selectedSummary.documentCount)}
          </Box>{" "}
          รายการ
        </Typography>

        <NoteSendTable
          notes={notes}
          loading={loading}
          page={page}
          limit={limit}
          selected={selected}
          onToggleNote={handleToggleNote}
          onToggleAllOnPage={handleToggleAllOnPage}
        />

        <ETaxPagination
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          limit={limit}
          setLimit={setLimit}
        />
      </Paper>

      <SendSummaryFooter
        summary={selectedSummary}
        countLabel="จำนวนเอกสาร"
        onConfirm={handleOpenConfirm}
      />

      <ConfirmSendModal
        open={confirmOpen}
        summary={selectedSummary}
        loading={sending}
        documentTypes={NOTE_DOC_TYPES}
        onCancel={handleCloseConfirm}
        onConfirm={handleConfirmSend}
      />
      <SuccessModal
        open={successOpen}
        result={sendResult}
        documentNoun={NOTE_NOUN}
        onClose={handleCloseSuccess}
      />
      <SendProblemModal
        problem={sendProblem}
        documentNoun={NOTE_NOUN}
        onClose={handleCloseProblem}
      />
    </Box>
  );
}
